import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as paymentApi from '../../api/payment';
import { message } from 'antd';

// 获取支付接口列表
export const fetchPaymentInterfaces = createAsyncThunk(
  'payment/fetchPaymentInterfaces',
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentApi.getPaymentInterfaceList();
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取支付接口列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取支付接口列表失败');
    }
  }
);

// 获取支付接口详情
export const fetchPaymentInterfaceDetail = createAsyncThunk(
  'payment/fetchPaymentInterfaceDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await paymentApi.getPaymentInterfaceDetail(id);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取支付接口详情失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取支付接口详情失败');
    }
  }
);

// 创建支付接口
export const createPaymentInterface = createAsyncThunk(
  'payment/createPaymentInterface',
  async (data, { rejectWithValue }) => {
    try {
      const response = await paymentApi.createPaymentInterface(data);
      if (response && response.code === 200) {
        message.success('创建支付接口成功');
        return response.data;
      }
      message.error(response?.message || '创建支付接口失败');
      return rejectWithValue(response?.message || '创建支付接口失败');
    } catch (error) {
      message.error(error.message || '创建支付接口失败');
      return rejectWithValue(error.message || '创建支付接口失败');
    }
  }
);

// 更新支付接口
export const updatePaymentInterface = createAsyncThunk(
  'payment/updatePaymentInterface',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await paymentApi.updatePaymentInterface(id, data);
      if (response && response.code === 200) {
        message.success('更新支付接口成功');
        return response.data;
      }
      message.error(response?.message || '更新支付接口失败');
      return rejectWithValue(response?.message || '更新支付接口失败');
    } catch (error) {
      message.error(error.message || '更新支付接口失败');
      return rejectWithValue(error.message || '更新支付接口失败');
    }
  }
);

// 删除支付接口
export const deletePaymentInterface = createAsyncThunk(
  'payment/deletePaymentInterface',
  async (id, { rejectWithValue }) => {
    try {
      const response = await paymentApi.deletePaymentInterface(id);
      if (response && response.code === 200) {
        message.success('删除支付接口成功');
        return id;
      }
      message.error(response?.message || '删除支付接口失败');
      return rejectWithValue(response?.message || '删除支付接口失败');
    } catch (error) {
      message.error(error.message || '删除支付接口失败');
      return rejectWithValue(error.message || '删除支付接口失败');
    }
  }
);

// 启用支付接口
export const enablePaymentInterface = createAsyncThunk(
  'payment/enablePaymentInterface',
  async (id, { rejectWithValue }) => {
    try {
      const response = await paymentApi.enablePaymentInterface(id);
      if (response && response.code === 200) {
        message.success('启用支付接口成功');
        return id;
      }
      message.error(response?.message || '启用支付接口失败');
      return rejectWithValue(response?.message || '启用支付接口失败');
    } catch (error) {
      message.error(error.message || '启用支付接口失败');
      return rejectWithValue(error.message || '启用支付接口失败');
    }
  }
);

// 禁用支付接口
export const disablePaymentInterface = createAsyncThunk(
  'payment/disablePaymentInterface',
  async (id, { rejectWithValue }) => {
    try {
      const response = await paymentApi.disablePaymentInterface(id);
      if (response && response.code === 200) {
        message.success('禁用支付接口成功');
        return id;
      }
      message.error(response?.message || '禁用支付接口失败');
      return rejectWithValue(response?.message || '禁用支付接口失败');
    } catch (error) {
      message.error(error.message || '禁用支付接口失败');
      return rejectWithValue(error.message || '禁用支付接口失败');
    }
  }
);

// 初始状态
const initialState = {
  paymentInterfaceList: {
    data: [],
    loading: false,
    error: null
  },
  paymentInterfaceDetail: {
    data: null,
    loading: false,
    error: null
  },
  createStatus: {
    loading: false,
    success: false,
    error: null
  },
  updateStatus: {
    loading: false,
    success: false,
    error: null
  },
  deleteStatus: {
    loading: false,
    success: false,
    error: null
  },
  enableDisableStatus: {
    loading: false,
    success: false,
    error: null
  }
};

// 创建slice
const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearCreateStatus: (state) => {
      state.createStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    clearUpdateStatus: (state) => {
      state.updateStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    clearDeleteStatus: (state) => {
      state.deleteStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    clearEnableDisableStatus: (state) => {
      state.enableDisableStatus = {
        loading: false,
        success: false,
        error: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取支付接口列表
      .addCase(fetchPaymentInterfaces.pending, (state) => {
        state.paymentInterfaceList.loading = true;
        state.paymentInterfaceList.error = null;
      })
      .addCase(fetchPaymentInterfaces.fulfilled, (state, action) => {
        state.paymentInterfaceList.loading = false;
        state.paymentInterfaceList.data = action.payload || [];
      })
      .addCase(fetchPaymentInterfaces.rejected, (state, action) => {
        state.paymentInterfaceList.loading = false;
        state.paymentInterfaceList.error = action.payload;
      })

      // 处理获取支付接口详情
      .addCase(fetchPaymentInterfaceDetail.pending, (state) => {
        state.paymentInterfaceDetail.loading = true;
        state.paymentInterfaceDetail.error = null;
      })
      .addCase(fetchPaymentInterfaceDetail.fulfilled, (state, action) => {
        state.paymentInterfaceDetail.loading = false;
        state.paymentInterfaceDetail.data = action.payload;
      })
      .addCase(fetchPaymentInterfaceDetail.rejected, (state, action) => {
        state.paymentInterfaceDetail.loading = false;
        state.paymentInterfaceDetail.error = action.payload;
      })

      // 处理创建支付接口
      .addCase(createPaymentInterface.pending, (state) => {
        state.createStatus.loading = true;
        state.createStatus.success = false;
        state.createStatus.error = null;
      })
      .addCase(createPaymentInterface.fulfilled, (state, action) => {
        state.createStatus.loading = false;
        state.createStatus.success = true;
        state.paymentInterfaceList.data.push(action.payload);
      })
      .addCase(createPaymentInterface.rejected, (state, action) => {
        state.createStatus.loading = false;
        state.createStatus.error = action.payload;
      })

      // 处理更新支付接口
      .addCase(updatePaymentInterface.pending, (state) => {
        state.updateStatus.loading = true;
        state.updateStatus.success = false;
        state.updateStatus.error = null;
      })
      .addCase(updatePaymentInterface.fulfilled, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.success = true;
        // 更新列表中的数据
        const index = state.paymentInterfaceList.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.paymentInterfaceList.data[index] = action.payload;
        }
        // 更新详情数据
        if (state.paymentInterfaceDetail.data && state.paymentInterfaceDetail.data.id === action.payload.id) {
          state.paymentInterfaceDetail.data = action.payload;
        }
      })
      .addCase(updatePaymentInterface.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      })

      // 处理删除支付接口
      .addCase(deletePaymentInterface.pending, (state) => {
        state.deleteStatus.loading = true;
        state.deleteStatus.success = false;
        state.deleteStatus.error = null;
      })
      .addCase(deletePaymentInterface.fulfilled, (state, action) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.success = true;
        // 从列表中删除数据
        state.paymentInterfaceList.data = state.paymentInterfaceList.data.filter(item => item.id !== action.payload);
        // 清空详情数据（如果正在查看被删除的接口）
        if (state.paymentInterfaceDetail.data && state.paymentInterfaceDetail.data.id === action.payload) {
          state.paymentInterfaceDetail.data = null;
        }
      })
      .addCase(deletePaymentInterface.rejected, (state, action) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.error = action.payload;
      })

      // 处理启用支付接口
      .addCase(enablePaymentInterface.pending, (state) => {
        state.enableDisableStatus.loading = true;
        state.enableDisableStatus.success = false;
        state.enableDisableStatus.error = null;
      })
      .addCase(enablePaymentInterface.fulfilled, (state, action) => {
        state.enableDisableStatus.loading = false;
        state.enableDisableStatus.success = true;
        // 更新列表中的数据
        const index = state.paymentInterfaceList.data.findIndex(item => item.id === action.payload);
        if (index !== -1) {
          state.paymentInterfaceList.data[index].status = 'ACTIVE';
        }
        // 更新详情数据
        if (state.paymentInterfaceDetail.data && state.paymentInterfaceDetail.data.id === action.payload) {
          state.paymentInterfaceDetail.data.status = 'ACTIVE';
        }
      })
      .addCase(enablePaymentInterface.rejected, (state, action) => {
        state.enableDisableStatus.loading = false;
        state.enableDisableStatus.error = action.payload;
      })

      // 处理禁用支付接口
      .addCase(disablePaymentInterface.pending, (state) => {
        state.enableDisableStatus.loading = true;
        state.enableDisableStatus.success = false;
        state.enableDisableStatus.error = null;
      })
      .addCase(disablePaymentInterface.fulfilled, (state, action) => {
        state.enableDisableStatus.loading = false;
        state.enableDisableStatus.success = true;
        // 更新列表中的数据
        const index = state.paymentInterfaceList.data.findIndex(item => item.id === action.payload);
        if (index !== -1) {
          state.paymentInterfaceList.data[index].status = 'INACTIVE';
        }
        // 更新详情数据
        if (state.paymentInterfaceDetail.data && state.paymentInterfaceDetail.data.id === action.payload) {
          state.paymentInterfaceDetail.data.status = 'INACTIVE';
        }
      })
      .addCase(disablePaymentInterface.rejected, (state, action) => {
        state.enableDisableStatus.loading = false;
        state.enableDisableStatus.error = action.payload;
      });
  }
});

export const { 
  clearCreateStatus, 
  clearUpdateStatus, 
  clearDeleteStatus, 
  clearEnableDisableStatus 
} = paymentSlice.actions;

export default paymentSlice.reducer; 